import React, { useState, useCallback, useEffect } from "react";
import customFetch from "../apicall/api";
import LifecycleTimeline from "./LifecycleTimeline";
import {
  fmtINR, fmtDate, txnRefNum,
  STATUS_COLOUR, INVOLVEMENT_COLOUR,
  COLLECTION_TYPE_LABEL,
  LOAN_STATUS_LABEL, LOAN_STATUS_COLOUR,
  calcEffectiveAmount,
} from "./helpers";

// Inline loan status badge — ACTIVE shows nothing
function LoanBadge({ loanStatus }) {
  var cls = LOAN_STATUS_COLOUR[loanStatus];
  if (!cls) return null;
  return (
    <span className={"loan-badge " + cls}>
      {LOAN_STATUS_LABEL[loanStatus] || loanStatus}
    </span>
  );
}

// History row list rendered inside the history panel
function HistoryList({ items, currentRef, emptyText }) {
  if (!items || items.length === 0) {
    return <div className="tm-history__empty">{emptyText}</div>;
  }
  return (
    <div className="tm-history__list">
      {items.map(function(item, idx) {
        var isCurrent = item.reference_number === currentRef;
        var badgeCls  = STATUS_COLOUR[item.status_label] || "status-badge--grey";
        return (
          <div
            key={idx}
            className={"tm-history__row" + (isCurrent ? " tm-history__row--current" : "") + (item.is_reversed ? " tm-history__row--reversed" : "")}
          >
            <div className="tm-history__row-left">
              <div className="tm-history__row-ref">
                {isCurrent && <span className="tm-history__current-dot">●</span>}
                {item.reference_number}
              </div>
              <div className="tm-history__row-meta">
                {item.household_name && <span>{item.household_name}</span>}
                {item.branch_name && <span className="tm-history__row-branch"> · {item.branch_name}</span>}
              </div>
              <div className="tm-history__row-date">
                {fmtDate(item.collection_date || item.added_at)}
                {item.added_by_name && <span> · {item.added_by_name}</span>}
              </div>
            </div>
            <div className="tm-history__row-right">
              <div className="tm-history__row-amount">
                ₹{fmtINR(item.amount_collected)}
              </div>
              <span className={"status-badge " + badgeCls}>{item.status_label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TransactionCard({ txn, expanded, onToggle, apiToken }) {
  var hasReversal  = txn.is_reversed;
  var isSwapped    = txn.swap_flag === "Y";
  var loans        = txn.loans || [];
  var hasLoans     = loans.length > 0;
  var hasAdjustments = hasLoans && loans.some(function(l) { return l.loan_status !== "ACTIVE"; });

  var effectiveAmt  = hasLoans ? calcEffectiveAmount(loans) : null;
  var effectiveValid = effectiveAmt !== null &&
    effectiveAmt !== (txn.total_amount_collected || txn.amount_collected);

  var badgeCls  = STATUS_COLOUR[txn.status_label] || "status-badge--grey";
  var invCls    = INVOLVEMENT_COLOUR[txn.involvement_type] || "inv-badge--grey";
  // Priority: txn_bank_ref_no → txn_ref_no → reference_number
  var refNum    = txnRefNum(txn);
  var rawAmt    = txn.total_amount_collected || txn.amount_collected;
  var loanCount = txn.loan_count || (hasLoans
    ? new Set(loans.map(function(l) { return l.loan_id; })).size : 0);

  var [slipLoading, setSlipLoading] = useState(false);
  var [slipDialogUrl, setSlipDialogUrl] = useState(null);

  var hasBatch = txn.txn_ref_no && !txn.txn_ref_no.startsWith("NDLC");

  var [batchData, setBatchData]       = useState(null);
  var [batchLoading, setBatchLoading] = useState(false);
  var [batchError, setBatchError]     = useState(null);

  useEffect(function() {
    if (!expanded || !hasBatch || batchData || batchLoading) return;
    setBatchLoading(true);
    setBatchError(null);
    var uid = localStorage.getItem("in_userid");
    customFetch(
      process.env.REACT_APP_URL + "/reversal/monitor/list?" + new URLSearchParams({
        userid:     uid,
        txn_ref_no: txn.txn_ref_no,
        page_size:  "50",
        skip:       "0",
      }).toString(),
      {
        method: "GET",
        headers: {
          "api-token": apiToken || localStorage.getItem("api_token") || "",
          in_userid: uid,
        },
      }
    )
      .then(function(res) { return res.json(); })
      .then(function(json) { setBatchData((json && json.data) || []); })
      .catch(function() { setBatchError("Error loading batch."); })
      .finally(function() { setBatchLoading(false); });
  }, [expanded, hasBatch, txn.txn_ref_no, batchData, batchLoading, apiToken]);

  function fetchSlipUrl(cb) {
    if (!txn.bank_slip_path || slipLoading) return;
    setSlipLoading(true);
    var uid = localStorage.getItem("in_userid");
    customFetch(
      process.env.REACT_APP_FILE_DOWNLOAD + "/gcs/download",
      {
        method: "POST",
        headers: {
          "api-token": apiToken || localStorage.getItem("api_token") || "",
          in_userid: uid,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ source_blob_name: txn.bank_slip_path }),
      }
    )
      .then(function(res) { return res.blob(); })
      .then(function(blob) { cb(URL.createObjectURL(blob)); })
      .catch(function() { alert("Error fetching slip."); })
      .finally(function() { setSlipLoading(false); });
  }

  function openSlipDialog() {
    if (slipDialogUrl) { return; }   // already loaded
    fetchSlipUrl(function(url) { setSlipDialogUrl(url); });
  }

  return (
    <div className={"tm-card" + (expanded ? " tm-card--expanded" : "")}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        className="tm-card__header"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={function(e) { if (e.key === "Enter") onToggle(); }}
      >
        <div className="tm-card__left">
          <div className="tm-card__txn-id">{refNum}</div>
          <div className="tm-card__name">{txn.household_name}</div>
        </div>
        <div className="tm-card__right">
          <span className={"status-badge " + badgeCls}>{txn.status_label}</span>
          <span className="tm-card__chevron">{expanded ? "∧" : "∨"}</span>
        </div>
      </div>

      {/* ── Amount row ─────────────────────────────────────────────────── */}
      <div className="tm-card__amount-row" onClick={onToggle}>
        <div>
          {(hasReversal || effectiveValid) ? (
            <div>
              <div className="tm-card__amount-badges">
                <span className="tm-card__amount-effective tm-card__amount--struck">
                  ₹{fmtINR(rawAmt)}
                </span>
                {isSwapped ? (
                  <span className="tm-chip tm-chip--yellow ml-6">Swapped</span>
                ) : (
                  <span className="tm-chip tm-chip--red ml-6">Reversed</span>
                )}
              </div>
              {effectiveAmt !== null && (
                <div className="tm-card__effective">
                  <span className="tm-card__effective-label">Effective</span>
                  <span className="tm-card__effective-amount">
                    ₹{fmtINR(effectiveAmt)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <span className="tm-card__amount-effective">
              ₹{fmtINR(rawAmt)}
            </span>
          )}
          <div className="tm-card__meta">
            {txn.collection_mode} · {fmtDate(txn.collection_date)}
            {loanCount > 0 && (
              <span className="tm-card__loan-count ml-6">
                {loanCount} loan{loanCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          {isSwapped && (
            <div className="tm-card__swap-ref">
              {txn.household_number && txn.swap_partner_hh_number
                ? "↔ " + txn.household_number + " → " + txn.swap_partner_hh_number
                : txn.swap_reference_no
                  ? "↔ Swapped to " + txn.swap_reference_no
                  : "↔ Swapped"}
            </div>
          )}
        </div>
        <div className="tm-card__meta-right">
          <span className={"inv-badge " + invCls}>{txn.involvement_type}</span>
          {txn.bank_slip_path && (
            <button
              className="tm-view-slip-btn"
              onClick={function(e) { e.stopPropagation(); openSlipDialog(); }}
              disabled={slipLoading}
            >
              🏦 {slipLoading ? "…" : "View Slip"}
            </button>
          )}
        </div>
      </div>

      {/* ── Chips row: per-loan chips + holder ──────────────────────────── */}
      <div className="tm-card__chips">
        {loans.map(function(loan, li) {
          var ls   = loan.loan_status;
          var isSwappedOut = ls === "SWAPPED_OUT";
          var isSwappedIn  = ls === "SWAPPED_IN";
          var isReversed   = ls === "REVERSED";
          var chipExtra = isSwappedOut ? " tm-chip--swapped-out"
                        : isSwappedIn  ? " tm-chip--swapped-in"
                        : isReversed   ? " tm-chip--loan-adj"
                        : "";
          return (
            <span
              key={li}
              className={"tm-chip tm-chip--loan" + chipExtra}
            >
              <span className={isSwappedOut ? "tm-chip__text--struck" : ""}>
                {loan.loan_id}: ₹{fmtINR(loan.amount)}
              </span>
              {isSwappedOut && (
                <span className="tm-chip__badge">{LOAN_STATUS_LABEL.SWAPPED_OUT}</span>
              )}
              {isSwappedIn && (
                <span className="tm-chip__badge tm-chip__badge--in">{LOAN_STATUS_LABEL.SWAPPED_IN}</span>
              )}
              {isReversed && (
                <span className="tm-chip__adj-mark"> ✕</span>
              )}
            </span>
          );
        })}
        {loans.length === 0 && txn.household_number && (
          <span className={"tm-chip " + (hasReversal ? "tm-chip--grey" : "tm-chip--blue")}>
            {txn.household_number}
          </span>
        )}
        {txn.current_cash_holder_name && (
          <span className="tm-chip tm-chip--holder">
            👤 {txn.current_cash_holder_name}
          </span>
        )}
      </div>

      {/* ── Expanded body ──────────────────────────────────────────────── */}
      {expanded && (
        <div className="tm-card__body">
          {/* Transaction Summary — only when adjustments exist */}
          {hasAdjustments && (
            <div className="tm-txn-summary">
              <div className="tm-section-title">TRANSACTION SUMMARY</div>
              <div className="tm-txn-summary__box">
                <div className="tm-txn-summary__row">
                  <span className="tm-txn-summary__label">Total Collected</span>
                  <span className="tm-txn-summary__val">₹{fmtINR(rawAmt)}</span>
                </div>
                <div className="tm-txn-summary__row tm-txn-summary__row--adj">
                  <span className="tm-txn-summary__label">
                    <span className="tm-txn-summary__warn">⚠</span>
                    {isSwapped ? "Swapped Out" : "Reversed"}
                  </span>
                  <span className="tm-txn-summary__val tm-txn-summary__val--neg">
                    −₹{fmtINR(rawAmt - (effectiveAmt || 0))}
                  </span>
                </div>
                <div className="tm-txn-summary__row tm-txn-summary__row--total">
                  <span className="tm-txn-summary__label">Effective Total</span>
                  <span className="tm-txn-summary__val tm-txn-summary__val--effective">
                    ₹{fmtINR(effectiveAmt || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Lifecycle */}
          <div className="tm-section-title">TRANSACTION LIFECYCLE</div>
          <LifecycleTimeline steps={txn.lifecycle} txnStatusCode={txn.status_code} />

          {/* Loan Breakdown */}
          {hasLoans && (
            <div>
              <div className="tm-section-title">LOAN BREAKDOWN</div>
              <div className="tm-breakdown">
                {loans.map(function(loan, li) {
                  var ls          = loan.loan_status;
                  var isSwappedOut = ls === "SWAPPED_OUT";
                  var isSwappedIn  = ls === "SWAPPED_IN";
                  var isReversed   = ls === "REVERSED";
                  var loanAdj      = ls && ls !== "ACTIVE";
                  var strikeAmount = isSwappedOut || isReversed;
                  var rowCls = "tm-breakdown__row" +
                    (isSwappedOut ? " tm-breakdown__row--swapped-out" :
                     isSwappedIn  ? " tm-breakdown__row--swapped-in"  :
                     loanAdj      ? " tm-breakdown__row--adj"         : "");
                  return (
                    <div key={li} className={rowCls}>
                      <div className="tm-breakdown__info">
                        {loan.customer_name && (
                          <div className="tm-breakdown__customer">{loan.customer_name}</div>
                        )}
                        <div className="tm-breakdown__loan-id">
                          {isSwappedOut && (
                            <span className="tm-breakdown__swap-icon">↔ </span>
                          )}
                          {isSwappedIn && (
                            <span className="tm-breakdown__swap-in-icon">↑ </span>
                          )}
                          {loan.loan_id}
                        </div>
                        {isSwappedIn && loan.swapped_from && (
                          <div className="tm-breakdown__swap-from">
                            {txn.household_number && txn.swap_partner_hh_number
                              ? txn.household_number + " → " + txn.swap_partner_hh_number
                              : "from: " + loan.swapped_from}
                          </div>
                        )}
                        <div className="tm-breakdown__type">
                          {COLLECTION_TYPE_LABEL[loan.collection_type] ||
                            loan.collection_type || "Payment"}
                        </div>
                        <LoanBadge loanStatus={loan.loan_status} />
                      </div>
                      <span className={
                        "tm-breakdown__amount" + (strikeAmount ? " tm-breakdown__amount--struck" : "")
                      }>
                        ₹{fmtINR(loan.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bank Deposit Slip */}
          {txn.bank_slip_path && (
            <div className="tm-bank-slip">
              <span className="tm-bank-slip__icon">🏦</span>
              <div className="tm-bank-slip__info">
                <span className="tm-bank-slip__label">Bank Deposit Slip</span>
                {txn.txn_ref_no_bank && (
                  <span className="tm-bank-slip__ref">Ref: {txn.txn_ref_no_bank}</span>
                )}
              </div>
              <button
                className="tm-bank-slip__btn"
                onClick={openSlipDialog}
                disabled={slipLoading}
              >
                {slipLoading ? "…" : "View"}
              </button>
            </div>
          )}

          {/* ── Batch Collections ─────────────────────────────────────────── */}
          {hasBatch && (
            <div className="tm-history">
              <div className="tm-section-title">BATCH COLLECTIONS ({txn.txn_ref_no})</div>
              {batchLoading && <div className="tm-history__loading">Loading…</div>}
              {batchError  && <div className="tm-history__error">{batchError}</div>}
              {batchData   && (
                <HistoryList
                  items={batchData}
                  currentRef={txn.reference_number}
                  emptyText="No other collections in this batch."
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Slip Dialog (inline modal) ───────────────────────────────────── */}
      {slipDialogUrl && (
        <div
          className="tm-slip-dialog-overlay"
          onClick={function() { setSlipDialogUrl(null); }}
        >
          <div
            className="tm-slip-dialog"
            onClick={function(e) { e.stopPropagation(); }}
          >
            <div className="tm-slip-dialog__header">
              <span>Bank Deposit Slip</span>
              <button
                className="tm-slip-dialog__close"
                onClick={function() { setSlipDialogUrl(null); }}
              >×</button>
            </div>
            <div className="tm-slip-dialog__body">
              {slipDialogUrl.toLowerCase().includes(".pdf") ? (
                <iframe
                  src={slipDialogUrl}
                  title="Bank Deposit Slip"
                  className="tm-slip-dialog__frame"
                />
              ) : (
                <img
                  src={slipDialogUrl}
                  alt="Bank Deposit Slip"
                  className="tm-slip-dialog__img"
                />
              )}
              {txn.txn_ref_no_bank && (
                <div className="tm-slip-dialog__meta">Bank Ref: {txn.txn_ref_no_bank}</div>
              )}
            </div>
            <div className="tm-slip-dialog__footer">
              <button
                className="tm-slip-dialog__open-btn"
                onClick={function() {
                  window.open(slipDialogUrl, "_blank", "noopener,noreferrer");
                }}
              >
                Open in New Tab ↗
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
